import { beginLoading } from '@/lib/loadingProgress';
import { clearAuthSession } from '@/lib/apiConfig';
import { recordTiming } from '@/lib/performance';
import { eventOfflineSnapshot, storeOfflineItem, readOfflineItem, removeOfflineItem, prepareOfflineAccess } from '@/lib/offlineStore';
import * as Sentry from '@sentry/react';
import { nativeRequestHeaders } from '@/lib/native';

class CustomClient {
  constructor({ apiUrl, token }) {
    this.apiUrl = apiUrl;
    this.token = token;

    this.entities = {};
    this.createApiMethods();
    this.auth = this.createAuthMethods();
    this.integrations = this.createIntegrations();
    this.documents = this.createDocumentMethods();
    this.trainingRecords = this.createTrainingRecordMethods();
    this.training = this.createTrainingMethods();
    this.exports = this.createExports();
    this.appLogs = this.createAppLogs();
    this.admin = this.createAdminMethods();
    this.settings = this.createSettingsMethods();
  }

  async fetchWithTimeout(url, options, timeoutMs = 30000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      return response;
    } catch (err) {
      const error = err.name === 'AbortError' ? new Error('Request timed out') : err;
      error.networkError = true;
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  async request(method, path, body = null, extraHeaders = {}, _isRetry = false) {
    const finishLoading = extraHeaders['X-SEP-Background'] || path.includes('/api/performance') ? () => {} : beginLoading();
    const start = performance.now();
    const page = window.location.pathname;
    let status = 200;
    try { return await this._request(method, path, body, extraHeaders, _isRetry); }
    catch (error) { status = error.status || 0; throw error; }
    finally { finishLoading(); if (extraHeaders['X-SEP-Background'] !== 'offline') recordTiming('api', performance.now() - start, `${method} ${path}`, status, page); }
  }

  async _request(method, path, body = null, extraHeaders = {}, _isRetry = false) {
    const headers = {
      'Content-Type': 'application/json',
      ...nativeRequestHeaders(),
      ...extraHeaders,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const impersonateUserId = localStorage.getItem('impersonate_user_id');
    if (impersonateUserId) {
      headers['X-Impersonate-User'] = impersonateUserId;
    }

    const options = {
      method,
      headers,
    };

    if (body && method !== 'GET') {
      if (body instanceof FormData) {
        delete headers['Content-Type'];
        options.body = body;
      } else {
        options.body = JSON.stringify(body);
      }
    }

    const response = await this.fetchWithTimeout(`${this.apiUrl}${path}`, options);

    if (response.status === 401) {
      let sessionRejected = path === '/auth/me';
      if (!_isRetry && !sessionRejected) {
        const meRes = await this.fetchWithTimeout(`${this.apiUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${this.token}`, ...nativeRequestHeaders() },
        });
        sessionRejected = meRes.status === 401;
        if (meRes.ok) {
          const me = await meRes.json();
          if (me.session_token) {
            this.token = me.session_token;
            localStorage.setItem('token', me.session_token);
          }
          return this.request(method, path, body, extraHeaders, true);
        }
        if (!sessionRejected) {
          const error = new Error('Unable to check your session. Please try again.');
          error.status = meRes.status;
          throw error;
        }
      }
      if (sessionRejected) {
        clearAuthSession();
        sessionStorage.setItem('explicit_logout', '1');
        this.token = null;
        if (!CustomClient._redirecting) {
          CustomClient._redirecting = true;
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?returnUrl=${returnUrl}`;
        }
      }
      const error = new Error(sessionRejected ? 'Please sign in again.' : 'This request was not authorised.');
      error.status = 401;
      throw error;
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: response.statusText }));
      const err = new Error(errorBody.message || errorBody.error || 'Request failed');
      err.status = response.status;
      if (errorBody.details) err.details = errorBody.details;
      if (errorBody.issues) err.issues = errorBody.issues;
      Sentry.captureException(err, {
        contexts: { api: { method, path, status: response.status } },
      });
      throw err;
    }

    return response.json();
  }

  createApiEntity(basePath) {
    return {
      list: (sort) => {
        const query = sort ? `?sort=${sort}` : '';
        return this.request('GET', `${basePath}${query}`);
      },
      get: (id) => {
        return this.request('GET', `${basePath}/${id}`);
      },
      create: (data) => {
        return this.request('POST', basePath, data);
      },
      update: (id, data) => {
        return this.request('PUT', `${basePath}/${id}`, data);
      },
      delete: (id) => {
        return this.request('DELETE', `${basePath}/${id}`);
      },
    };
  }

  createApiEntityWithQuery(basePath) {
    return {
      list: (paramsOrSort) => {
        const query = new URLSearchParams();
        if (typeof paramsOrSort === 'string') {
          query.set('sort', paramsOrSort);
        } else if (paramsOrSort && typeof paramsOrSort === 'object') {
          for (const [k, v] of Object.entries(paramsOrSort)) {
            if (v !== undefined && v !== null) query.set(k, v);
          }
        }
        const qs = query.toString();
        return this.request('GET', `${basePath}${qs ? '?' + qs : ''}`);
      },
      get: (id) => this.request('GET', `${basePath}/${id}`),
      create: (data) => this.request('POST', basePath, data),
      update: (id, data) => this.request('PUT', `${basePath}/${id}`, data),
      delete: (id) => this.request('DELETE', `${basePath}/${id}`),
    };
  }

  createApiMethods() {
    this.entities.NotificationTemplate = this.createApiEntity('/api/notification-templates');
    this.entities.NotificationLog = this.createApiEntity('/api/notification-logs');
    this.entities.DocumentType = this.createApiEntity('/api/document-types');
    this.entities.TrainingType = this.createApiEntity('/api/training-types');
    this.entities.Notification = {
      ...this.createApiEntity('/api/notifications'),
      markRead: (id) => this.request('PUT', `/api/notifications/${id}/read`),
      markAllRead: () => this.request('PUT', '/api/notifications/read-all'),
      listByUser: (email) => this.request('GET', `/api/notifications/by-user?user_email=${encodeURIComponent(email)}`),
    };

    this.entities.EventInvite = this.createApiEntity('/api/event-invites');

    this.entities.Task = this.createApiEntityWithQuery('/api/tasks');
    this.entities.EventQuestionnaire = this.createApiEntityWithQuery('/api/questionnaires');
    this.entities.ClockEntry = this.createApiEntityWithQuery('/api/clock-entries');

    this.entities.EventStaffing = {
      ...this.createApiEntityWithQuery('/api/staffing'),
      reorder: (ids) => this.request('PUT', '/api/staffing/reorder', { staffing_ids: ids }),
      requestDates: (id, data) => this.request('POST', `/api/staffing/${id}/date-change-request`, data),
      reviewDates: (id, data) => this.request('PUT', `/api/staffing/${id}/date-change-request`, data),
    };
    this.entities.Event = {
      ...this.createApiEntityWithQuery('/api/events'),
      get: async id => {
        const requestOwner = localStorage.getItem('sep-offline-owner');
        const requestToken = this.token;
        try {
          const event = await this.request('GET', `/api/events/${id}`);
          const owner = localStorage.getItem('sep-offline-owner');
          if (owner && owner === requestOwner && this.token === requestToken && !localStorage.getItem('impersonate_user_id')) {
            await storeOfflineItem(eventOfflineSnapshot(event, owner)).catch(() => {});
            prepareOfflineAccess().catch(() => {});
          }
          return event;
        } catch (error) {
          if ([403, 404].includes(error.status)) await removeOfflineItem(`event:${id}`).catch(() => {});
          if (error.networkError && requestOwner === localStorage.getItem('sep-offline-owner') && requestToken === this.token) {
            const saved = await readOfflineItem(`event:${id}`).catch(() => null);
            if (saved) return { ...saved.details, id, _id: id, _offlineSnapshot: saved };
          }
          throw error;
        }
      },
      team: (id) => this.request('GET', `/api/events/${id}/team`),
      requestStaff: (id, data) => this.request('POST', `/api/events/${id}/request-staff`, data),
    };
    this.entities.User = {
      ...this.createApiEntityWithQuery('/api/users'),
      getManagerNotes: (id) => this.request('GET', `/api/users/${id}/manager-notes`),
      saveManagerNotes: (id, notes) => this.request('PUT', `/api/users/${id}/manager-notes`, { notes }),
      requestPasswordReset: (id) => this.request('POST', `/api/users/${id}/password-reset`),
      freelancerProfiles: () => this.request('GET', '/api/users/freelancer-profiles'),
      getAvailability: (id) => this.request('GET', `/api/users/${id}/availability`),
      updateProfile: (id, data, sensitiveToken) => this.request('PUT', `/api/users/${id}/profile`, data, sensitiveToken ? { 'X-Sensitive-Token': sensitiveToken } : {}),
      getSensitive: (id, sensitiveToken) => this.request('GET', `/api/users/${id}`, null, { 'X-Sensitive-Token': sensitiveToken }),
      mergeCandidates: () => this.request('GET', '/api/users/merge-candidates'),
      mergeUsers: (primaryId, secondaryId) => this.request('POST', '/api/users/merge', { primary_user_id: primaryId, secondary_user_id: secondaryId, same_person_confirmed: true }),
    };

    this.entities.Skill = {
      ...this.createApiEntity('/api/skills'),
      reorder: (ids) => this.request('PUT', '/api/skills/reorder', { skill_ids: ids }),
    };

    this.entities.FlightItinerary = {
      realtime: (eventId) => this.request('GET', `/api/flight-itineraries/realtime${eventId ? `?event_id=${encodeURIComponent(eventId)}` : ''}`),
      ...this.createApiEntityWithQuery('/api/flight-itineraries'),
      directory: () => this.request('GET', '/api/flight-itineraries/directory'),
      rematch: (id) => this.request('POST', `/api/flight-itineraries/${id}/rematch`),
      uploadTicket: (id, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return this.request('POST', `/api/flight-itineraries/${id}/upload-ticket`, formData);
      },
      downloadTicket: (id) => this.request('GET', `/api/flight-itineraries/${id}/ticket-download`),
      downloadAttachment: (id, index) => this.request('GET', `/api/flight-itineraries/${id}/attachment-download/${index}`),
    };
  }

  createAuthMethods() {
    return {
      me: () => {
        return this.request('GET', '/auth/me');
      },
      updateMe: (data) => {
        return this.request('PUT', '/auth/me', data);
      },
      logout: (returnUrl) => {
        localStorage.removeItem('token');
        if (returnUrl) {
          window.location.href = returnUrl;
        }
      },
      addPassword: (password) => {
        return this.request('POST', '/auth/add-password', { password });
      },
      linkAccount: (password) => {
        return this.request('POST', '/auth/link-account', { password });
      },
      changePassword: (currentPassword, newPassword) => {
        return this.request('POST', '/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      },
      forgotPassword: (email) => {
        return this.request('POST', '/auth/forgot-password', { email });
      },
      redirectToLogin: (returnUrl) => {
        const loginUrl = returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login';
        window.location.href = loginUrl;
      },
      emails: {
        add: (email) => this.request('POST', '/auth/emails/add', { email }),
        verify: (token) => this.request('POST', '/auth/emails/verify', { token }),
        resendVerification: (email) => this.request('POST', '/auth/emails/resend-verification', { email }),
        setPrimary: (email) => this.request('PUT', '/auth/emails/set-primary', { email }),
        remove: (email) => this.request('POST', '/auth/emails/remove', { email }),
      },
      twoFactor: {
        setup: () => this.request('POST', '/auth/2fa/setup'),
        verify: (code) => this.request('POST', '/auth/2fa/verify', { code }),
        disable: (code, password) => this.request('POST', '/auth/2fa/disable', { code, password }),
      },
    };
  }

  createIntegrations() {
    const methods = {
      SendEmail: ({ to, subject, body }) => {
        return this.request('POST', '/integrations/email', { to, subject, body });
      },
      UploadFile: async ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return this.request('POST', '/integrations/upload', formData);
      },
      InvokeLLM: ({ prompt, response_json_schema }) => {
        return this.request('POST', '/integrations/llm', { prompt, response_json_schema });
      },
      SendBatchEmail: ({ recipients, subject, body, sendEmail, sendNotification, linkedDocIds = [], attachments = [] }) => {
        const formData = new FormData();
        formData.append('recipients', JSON.stringify(recipients));
        formData.append('subject', subject);
        formData.append('body', body || '');
        formData.append('sendEmail', String(!!sendEmail));
        formData.append('sendNotification', String(!!sendNotification));
        formData.append('linkedDocIds', JSON.stringify(linkedDocIds));
        for (const file of attachments) {
          formData.append('attachments', file);
        }
        return this.request('POST', '/integrations/email/batch', formData);
      }
    };
    return {
      ...methods,
      Core: methods
    };
  }

  createDocumentMethods() {
    return {
      upload: (file, { scope, document_type, target_user_email, event_id, notify_users, requires_signature }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('scope', scope);
        formData.append('document_type', document_type);
        if (target_user_email) formData.append('target_user_email', target_user_email);
        if (event_id) formData.append('event_id', event_id);
        if (notify_users) formData.append('notify_users', 'true');
        if (requires_signature) formData.append('requires_signature', 'true');
        return this.request('POST', '/api/documents/upload', formData);
      },
      createLink: ({ document_url, original_filename, document_type, scope, target_user_email, event_id, notify_users }) => {
        const body = { document_url, original_filename, document_type, scope };
        if (target_user_email) body.target_user_email = target_user_email;
        if (event_id) body.event_id = event_id;
        if (notify_users) body.notify_users = true;
        return this.request('POST', '/api/documents/link', body);
      },
      list: (params = {}) => {
        const query = new URLSearchParams();
        if (params.scope) query.set('scope', params.scope);
        if (params.event_id) query.set('event_id', params.event_id);
        if (params.target_user_email) query.set('target_user_email', params.target_user_email);
        const qs = query.toString();
        return this.request('GET', `/api/documents${qs ? '?' + qs : ''}`);
      },
      getDownloadUrl: (id) => {
        return this.request('GET', `/api/documents/${id}/download`);
      },
      delete: (id) => {
        return this.request('DELETE', `/api/documents/${id}`);
      },
      sign: (id, signatureName) => {
        return this.request('POST', `/api/documents/${id}/sign`, { signature_name: signatureName });
      },
      getSignatures: (id) => {
        return this.request('GET', `/api/documents/${id}/signatures`);
      },
    };
  }

  createTrainingRecordMethods() {
    return {
      upload: (file, { training_type, completed_date }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('training_type', training_type);
        if (completed_date) formData.append('completed_date', completed_date);
        return this.request('POST', '/api/training-records/upload', formData);
      },
      list: () => {
        return this.request('GET', '/api/training-records');
      },
      listByUser: (email) => {
        return this.request('GET', `/api/training-records?user_email=${encodeURIComponent(email)}`);
      },
      download: (id) => {
        return this.request('GET', `/api/training-records/${id}/download`);
      },
      delete: (id) => {
        return this.request('DELETE', `/api/training-records/${id}`);
      },
    };
  }

  createTrainingMethods() {
    return {
      getLoginLink: () => {
        return this.request('GET', '/api/training/login-link');
      },
      getResults: () => {
        return this.request('GET', '/api/training/results');
      },
      getCourses: () => {
        return this.request('GET', '/api/training/courses');
      },
      adminGetUsers: () => {
        return this.request('GET', '/api/training/admin/users');
      },
      adminLinkUser: (userId, ihascoId) => {
        return this.request('PATCH', `/api/training/admin/users/${userId}/link`, { ihasco_id: ihascoId });
      },
      adminUnlinkUser: (userId) => {
        return this.request('DELETE', `/api/training/admin/users/${userId}/link`);
      },
      adminGetResults: (userId) => {
        return this.request('GET', `/api/training/admin/results/${userId}`);
      },
      adminSyncUsers: () => {
        return this.request('POST', '/jobs/run/sync-ihasco-users');
      },
      adminSyncCourses: () => {
        return this.request('POST', '/jobs/run/sync-ihasco-courses');
      },
      adminGetIhascoUsers: () => {
        return this.request('GET', '/api/training/admin/ihasco-users');
      },
      adminFetchIhascoUsers: () => {
        return this.request('POST', '/api/training/admin/ihasco-users/fetch');
      },
      adminHideIhascoUser: (ihascoId, hidden) => {
        return this.request('PATCH', `/api/training/admin/ihasco-users/${ihascoId}/hide`, { hidden });
      },
      getUserResults: (userId) => {
        return this.request('GET', `/api/training/user-results/${userId}`);
      },
    };
  }

  async downloadFile(path, sensitiveToken) {
    const headers = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    if (sensitiveToken) headers['X-Sensitive-Token'] = sensitiveToken;

    const response = await this.fetchWithTimeout(`${this.apiUrl}${path}`, { headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || error.error || 'Download failed');
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'export.xlsx';

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  createExports() {
    return {
      staffXlsx: (sensitiveToken, eventId) => {
        const path = eventId ? `/api/export/staff?event_id=${eventId}` : '/api/export/staff';
        return this.downloadFile(path, sensitiveToken);
      },
      clockEntriesXlsx: ({ eventId, from, to } = {}) => {
        const params = new URLSearchParams();
        if (eventId) params.set('event_id', eventId);
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        const qs = params.toString();
        return this.downloadFile(`/api/export/clock-entries${qs ? `?${qs}` : ''}`);
      }
    };
  }

  createAdminMethods() {
    return {
      queueStatus: () => this.request('GET', '/api/queue/status'),
      queueWaiting: (page = 0, pageSize = 20) => this.request('GET', `/api/queue/waiting?page=${page}&pageSize=${pageSize}`),
      queueFailed: (page = 0, pageSize = 20) => this.request('GET', `/api/queue/failed?page=${page}&pageSize=${pageSize}`),
      queueSent: (page = 0, pageSize = 20) => this.request('GET', `/api/queue/sent?page=${page}&pageSize=${pageSize}`),
      queueJob: (id) => this.request('GET', `/api/queue/job/${id}`),
      queueSentItem: (id) => this.request('GET', `/api/queue/sent/${id}`),
      queueForward: (source, id, toUserId) => this.request('POST', '/api/queue/forward', { source, id, ...(toUserId ? { to_user_id: toUserId } : {}) }),
      queueSendNow: (id) => this.request('POST', `/api/queue/job/${id}/send-now`),
      queueClearWaiting: () => this.request('DELETE', '/api/queue/waiting'),
      queueClearAll: () => this.request('DELETE', '/api/queue/jobs'),
      queueRetryAll: () => this.request('POST', '/api/queue/retry-all'),
      queuePause: () => this.request('POST', '/api/queue/pause'),
      queueResume: () => this.request('POST', '/api/queue/resume'),
      queueRemoveJob: (id) => this.request('DELETE', `/api/queue/job/${id}`),
      inboundEmails: (page = 0, pageSize = 20) => this.request('GET', `/api/admin/inbound-emails?page=${page}&pageSize=${pageSize}`),
      inboundEmail: (id) => this.request('GET', `/api/admin/inbound-emails/${id}`),
      markInboundRead: (id) => this.request('PUT', `/api/admin/inbound-emails/${id}/read`),
      parseFlightItinerary: (id) => this.request('POST', `/api/admin/inbound-emails/${id}/parse-flight`),
      refetchInboundEmail: (id) => this.request('POST', `/api/admin/inbound-emails/${id}/refetch`),
      deleteInboundEmail: (id) => this.request('DELETE', `/api/admin/inbound-emails/${id}`),
      auditLogs: (params = {}) => {
        const query = new URLSearchParams();
        if (params.issues) query.set('issues', params.issues);
        if (params.page !== undefined) query.set('page', params.page);
        if (params.pageSize) query.set('pageSize', params.pageSize);
        if (params.user_id) query.set('user_id', params.user_id);
        if (params.from) query.set('from', params.from);
        if (params.to) query.set('to', params.to);
        const qs = query.toString();
        return this.request('GET', `/api/audit-logs${qs ? '?' + qs : ''}`);
      },
      auditLogUsers: () => this.request('GET', '/api/audit-logs/users'),
      backupsList: () => this.request('GET', '/api/admin/backups'),
      backupsCreate: () => this.request('POST', '/api/admin/backups'),
      backupsDownload: (name) => this.request('GET', `/api/admin/backups/${encodeURIComponent(name)}/download`),
      backupsDelete: (name) => this.request('DELETE', `/api/admin/backups/${encodeURIComponent(name)}`),
    };
  }

  createSettingsMethods() {
    return {
      listEventCategories: () => this.request('GET', '/api/settings/event-categories'),
      createEventCategory: data => this.request('POST', '/api/settings/event-categories', data),
      updateEventCategory: (id, data) => this.request('PUT', `/api/settings/event-categories/${id}`, data),
      getSettings: () => this.request('GET', '/api/settings'),
      updateAirports: (airports) => this.request('PUT', '/api/settings/airports', { airports }),
      getSecurity: () => this.request('GET', '/api/settings/security'),
      getFlightRadar: () => this.request('GET', '/api/settings/flight-radar'),
      updateFlightRadar: (settings) => this.request('PUT', '/api/settings/flight-radar', typeof settings === 'boolean' ? { enabled: settings } : settings),
      updateTurnstile: (enabled) => this.request('PUT', '/api/settings/security/turnstile', { enabled }),
    };
  }

  createAppLogs() {
    return {
      logUserInApp: (pageName) => {
        return Promise.resolve();
      }
    };
  }
}

export function createClient({ apiUrl, token }) {
  return new CustomClient({ apiUrl, token });
}
